import type { SolutionVersionInput } from '@pypestream/api-services/urql';

/**
 * Writing the solution-level text of an agent (`otherInformation`,
 * `knowledgeAgentContext`) through `updateSolutionVersion`.
 *
 * That mutation takes the WHOLE version, not a patch: the backend deletes every
 * task the input does not list and every file it does not list, and it stores
 * `settings` as null when the input has none (read in the backend's
 * `update-solution-version.ts`, pypestream/backend at cd96d028; not run from
 * this repository). So a write that only names the
 * two text fields would wipe the version, and the tasks, files and numbers of
 * the version have to travel with the text. Taken from a copy that was loaded
 * when the panel opened, they put back whatever another editor changed since.
 * Fields the mutation takes but `SolutionVersionFields` does not select (the
 * version's `settings`) are not carried by this or any other writer in this
 * app, so the backend nulls them on every write; that is not changed here.
 *
 * What this does instead, for every save: wait until the machine can take the
 * event, read the version as the backend holds it now, derive the new text from
 * the stored text (not from the panel's copy of it), and send the stored version
 * with only the changed text replaced. The mutation has no version check, so a
 * change that lands between that read and the write is still replaced; the
 * window is one round trip instead of the whole time the panel was open.
 *
 * Limits: the other writers of a version (`updateSolutionForTaskRow`,
 * `deleteSolutionTask`, the Supervisor form) still send a copy from the
 * machine's context or from their form; a text that becomes empty is dropped
 * by `removeEmptyStrings` in the `updateSolution` invoke, so clearing a field
 * through this path is not stored although the save reports success.
 */

/** The two solution-level texts older saves kept each agent's block in. */
export interface VersionText {
  otherInformation: string;
  knowledgeAgentContext: string;
}

export type VersionTextChange = Partial<VersionText>;

interface StoredText {
  otherInformation?: string | null;
  knowledgeAgentContext?: string | null;
}

/** Everything a write reaches, so it can be driven without the machine. */
export interface WriteDeps<V extends StoredText> {
  /** Resolves when the machine takes `updateSolution`; rejects when it cannot. */
  waitForIdle: () => Promise<void>;
  isIdle: () => boolean;
  /**
   * The version the machine would write to right now: `updateSolution` takes
   * its id from the machine's context at the moment it runs, not from this
   * write's input.
   */
  currentVersionId: () => string | undefined;
  /** The version as the backend holds it now. */
  read: (versionId: string) => Promise<V | undefined>;
  /** The version as a mutation input; `undefined` when its files did not load. */
  toInput: (version: V) => SolutionVersionInput | undefined;
  /** Sends the write; `done` is called once the machine has applied it. */
  send: (version: SolutionVersionInput, done: () => void) => void;
  notify: (text: string, autoClose: number) => void;
  setSaving: (saving: boolean) => void;
  /** How long to wait for `done` before telling the user it failed. */
  giveUpMs: number;
}

/** Reads of the stored version one save may spend on a machine that keeps getting busy. */
const MAX_READS = 3;

const MESSAGES = {
  busy: 'Couldn’t save the agent settings: Pro Studio is busy. Try again.',
  read: 'Couldn’t save the agent settings: the solution couldn’t be read. Try again.',
  files:
    'Couldn’t save the agent settings: the solution’s files haven’t loaded. Reload and try again.',
  gaveUp: 'Couldn’t save the agent settings. Try again.',
  moved:
    'Couldn’t save the agent settings: another solution was opened meanwhile. Open this one again and retry.',
} as const;

const textOf = (stored: StoredText): VersionText => ({
  otherInformation: stored.otherInformation ?? '',
  knowledgeAgentContext: stored.knowledgeAgentContext ?? '',
});

/** The fields of `wanted` whose text differs from what is stored. */
const changesOf = (
  stored: VersionText,
  wanted: VersionTextChange
): VersionTextChange => {
  const changed: VersionTextChange = {};
  (Object.keys(wanted) as (keyof VersionText)[]).forEach((key) => {
    const next = wanted[key];
    if (next !== undefined && next !== stored[key]) changed[key] = next;
  });
  return changed;
};

const writeOnce = async <V extends StoredText>(
  versionId: string,
  derive: (stored: VersionText) => VersionTextChange,
  deps: WriteDeps<V>
): Promise<boolean> => {
  for (let reads = 0; reads < MAX_READS; reads += 1) {
    try {
      await deps.waitForIdle();
    } catch {
      deps.notify(MESSAGES.busy, 5000);
      return false;
    }
    let stored: V | undefined;
    try {
      stored = await deps.read(versionId);
    } catch {
      deps.notify(MESSAGES.read, 5000);
      return false;
    }
    if (!stored) {
      deps.notify(MESSAGES.read, 5000);
      return false;
    }
    // The machine drops `updateSolution` unless it is idle, and a write that
    // started while this read was in flight is not in `stored`: read again
    // once that one has finished instead of sending over it.
    if (!deps.isIdle()) continue;
    // The machine writes to the version of ITS context, so a write read for
    // another version must not be sent: it would replace that version's tasks
    // and files with this one's.
    if (deps.currentVersionId() !== versionId) {
      deps.notify(MESSAGES.moved, 6000);
      return false;
    }

    let changed: VersionTextChange;
    let base: SolutionVersionInput | undefined;
    try {
      changed = changesOf(textOf(stored), derive(textOf(stored)));
      if (!Object.keys(changed).length) return true;
      base = deps.toInput(stored);
    } catch {
      deps.notify(MESSAGES.gaveUp, 5000);
      return false;
    }
    if (!base) {
      deps.notify(MESSAGES.files, 6000);
      return false;
    }
    const input = base;

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(giveUp);
        resolve(ok);
      };
      // `updateSolution` calls back only on success; a failure lands in the
      // machine's error state, which reports it. Don't wait forever for it.
      const giveUp = setTimeout(() => {
        deps.notify(MESSAGES.gaveUp, 5000);
        finish(false);
      }, deps.giveUpMs);
      deps.send({ ...input, ...changed }, () => finish(true));
    });
  }
  deps.notify(MESSAGES.busy, 5000);
  return false;
};

/** Saves run one after another: the next one starts from what the last one left. */
let queue: Promise<unknown> = Promise.resolve();
/** Saves started and not yet settled, for the `saving` flag. */
let unsettled = 0;

/**
 * Replaces the text `derive` names, in the version as stored when the save
 * runs. Resolves `true` when the text is stored (also when it already was) and
 * `false` when it is not, after telling the user.
 *
 * A save that gave up after `giveUpMs` may still land later, so the next one
 * waits for the machine to be idle again before it reads: it then sees that
 * write, or finds the machine still busy with it, rather than sending a copy
 * from before it. The panel's Save button is disabled while the machine is
 * updating the solution (`extras.saving`), so from that button a second save
 * cannot start during the first (measured live); the queue and the idle wait
 * cover a caller that has no such button.
 */
export const writeVersionText = <V extends StoredText>(
  versionId: string,
  derive: (stored: VersionText) => VersionTextChange,
  deps: WriteDeps<V>
): Promise<boolean> => {
  // Raised when the save is asked for, not when it reaches the wire: the wait
  // for the machine and the read can take a while, and Save has to read as busy.
  deps.setSaving(true);
  unsettled += 1;
  const run = queue.then(() => writeOnce(versionId, derive, deps));
  queue = run.catch(() => undefined);
  const settle = () => {
    unsettled -= 1;
    if (unsettled === 0) deps.setSaving(false);
  };
  run.then(settle, settle);
  return run;
};

/**
 * The `derive` of a save: each named field becomes `transforms[field]` applied
 * to the text as stored, the others are left out of the change.
 */
export const buildTextDerive =
  (
    transforms: Partial<Record<keyof VersionText, (stored: string) => string>>
  ) =>
  (stored: VersionText): VersionTextChange =>
    Object.fromEntries(
      (Object.keys(transforms) as (keyof VersionText)[]).map((field) => [
        field,
        transforms[field]?.(stored[field]),
      ])
    );

/**
 * The panel's two saves in the order that keeps them from undoing each other:
 * the tasks and documents write sends whole-form values, among them the two
 * texts as the form loaded them, so it goes first and the text save then reads
 * what it left. `extras` returns after it has sent its event (or without
 * sending one, when a file upload failed), and the text save waits for the
 * machine to finish that event before it reads.
 */
export const saveExtrasBeforeSettings = async (steps: {
  extras?: () => Promise<unknown>;
  settings?: () => Promise<unknown>;
}): Promise<void> => {
  try {
    if (steps.extras) await steps.extras();
  } finally {
    // Settings were saved first before this order was chosen, independent of
    // the tasks save: a throw from it does not stop them.
    if (steps.settings) await steps.settings();
  }
};
